import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { VariableSelector } from "../if/VariableSelector";
import { OperationSelector } from "../if/OperationSelector";
import { ComparativeValueInput } from "../if/ComparativeValueInput";
import { cn } from "@/utils/theme";
import type { Condition } from "@/workflow/types/Switch";
import type { NodeVariable } from "../types";

interface ConditionsProps {
  conditions: Condition[];
  variables: NodeVariable[];
  areConditionsValid: boolean;
  onAddCondition: () => void;
  onDeleteCondition: (id: string) => void;
  onUpdateCondition: (id: string, condition: Condition) => void;
}

const MAX_CONDITIONS = 10;

const Conditions = ({
  conditions = [],
  variables,
  areConditionsValid,
  onAddCondition,
  onDeleteCondition,
  onUpdateCondition,
}: ConditionsProps) => {
  const handleNewCondition = () => {
    if (conditions.length >= MAX_CONDITIONS) return;

    //validate existing conditions before adding a new one
    const hasEmptyCondition = conditions.some(
      (c) =>
        !c.variable ||
        !c.operation ||
        (c.operation.needsComparativeValue &&
          (c.comparativeValue === null ||
            c.comparativeValue === undefined ||
            c.comparativeValue === "")),
    );
    if (hasEmptyCondition) return;
    onAddCondition();
  };
  const handleDeleteCondition = (id: string) => {
    onDeleteCondition(id);
  };

  return (
    <div className="w-full flex flex-col gap-6 p-6">
      {conditions.map((c, index) => {
        return (
          <div
            key={c.id}
            className={cn(
              "w-full grid gap-2",
              index === 0 && "grid-cols-[1fr]",
              index > 0 && "grid-cols-[1fr_32px] place-items-center",
            )}
          >
            <div className="w-full flex flex-col gap-2">
              <GroupTextField
                label={`Salida ${index + 1}`}
                required
                className="w-full"
              >
                <GroupTextFieldRow className="h-8">
                  <div className="w-full relative">
                    <Icon
                      type="far"
                      name="input-text"
                      className="absolute left-2 translate-y-1/2 text-body"
                    />
                    <Input
                      variant="xs"
                      className="w-full pl-9"
                      group="up"
                      maxLength={255}
                      placeholder="Escribe el nombre de la salida"
                      value={c.label || ""}
                      onChange={(e) => {
                        onUpdateCondition(c.id, {
                          ...c,
                          label: e.target.value,
                        } as Condition);
                      }}
                    />
                  </div>
                </GroupTextFieldRow>
                <GroupTextFieldRow>
                  <VariableSelector
                    variables={variables}
                    selectedVariable={c.variable}
                    onSelectVariable={(v) => {
                      if (!v) return;
                      onUpdateCondition(c.id, {
                        ...c,
                        variable: v,
                        operation: null, // Reset operation when variable changes
                        comparativeValue: null, // Reset comparative value when variable changes
                        comparativeValueType: "input", // Reset to input when variable changes
                      } as Condition);
                    }}
                    triggerGroup={
                      c.operation?.needsComparativeValue
                        ? "middle-left"
                        : "down-left"
                    }
                  />
                  <OperationSelector
                    selectedVariable={c.variable}
                    selectedOperation={c.operation}
                    onSelectOperation={(op) => {
                      if (!op) return;
                      onUpdateCondition(c.id, {
                        ...c,
                        operation: op,
                        comparativeValue: null, // Reset comparative value when operation changes
                        comparativeValueType: "input", // Reset to input when operation changes
                      } as Condition);
                    }}
                    group={
                      c.operation?.needsComparativeValue
                        ? "middle-right"
                        : "down-right"
                    }
                    disabled={!c.variable}
                  />
                </GroupTextFieldRow>
                {c.operation?.needsComparativeValue && (
                  <GroupTextFieldRow>
                    <Button
                      variant="quiet"
                      type="button"
                      className="font-mono"
                      data-testid="toggle-comparative-value-type-btn"
                      onClick={() => {
                        const newType =
                          (c.comparativeValueType || "input") === "input"
                            ? "variable"
                            : "input";
                        onUpdateCondition(c.id, {
                          ...c,
                          comparativeValueType: newType,
                          comparativeValue: null, // Reset value when switching type
                        } as Condition);
                      }}
                    >
                      {(c.comparativeValueType || "input") === "input"
                        ? "inp"
                        : "var"}
                    </Button>
                    <ComparativeValueInput
                      selectedVariable={c.variable}
                      selectedOperation={{
                        ...c.operation,
                        comparativeValueType:
                          (c.comparativeValueType || "input") === "variable"
                            ? "variable"
                            : c.operation.comparativeValueType,
                      }}
                      comparativeValue={c.comparativeValue || ""}
                      onValueChange={(value) => {
                        onUpdateCondition(c.id, {
                          ...c,
                          comparativeValue: value,
                        } as Condition);
                      }}
                      variables={variables}
                    />
                  </GroupTextFieldRow>
                )}
              </GroupTextField>
            </div>
            {index > 0 && (
              <div className="mt-5">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Eliminar condición"
                  className="size-7"
                  type="button"
                  onClick={() => handleDeleteCondition(c.id)}
                >
                  <Icon type="far" name="times" />
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {conditions.length < MAX_CONDITIONS && (
        <div className="w-full flex justify-start">
          <Button
            variant="tertiary"
            onClick={handleNewCondition}
            disabled={!areConditionsValid}
          >
            Añadir salida
            <Icon type="far" name="plus" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Conditions;
